import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  SignOut,
  Users,
  ShieldCheck,
  Plus,
  Trash,
  PencilSimple,
  FloppyDisk,
  CircleNotch,
  X,
  Key,
  CaretDown,
  FilePdf,
  Eye,
  House,
  CaretDoubleLeft,
  CaretDoubleRight,
  MagnifyingGlass,
  Bell,
  ChatCircle,
  Wallet,
  Umbrella,
  Clock,
  Receipt,
  HandCoins,
  ChartLineUp,
  Car,
  Cpu,
  Gavel,
  AddressBook,
  ClockCounterClockwise,
  MapPin,
  BellRinging,
  Gear,
  CalendarCheck,
  CalendarDots,
  Envelope,
  FileText,
} from "@phosphor-icons/react";
import axios from "../utils/axiosInstance";  // Use axios with auth interceptor
import jsPDF from "jspdf";
import "jspdf-autotable";
import ChangePasswordModal from "../components/ChangePasswordModal";

// Import tab components
import EmployeesTab from "../components/admin/EmployeesTab";
import SalaryTab from "../components/admin/SalaryTab";
import PrivateCreditsTab from "../components/admin/PrivateCreditsTab";
import CorporateCreditsTab from "../components/admin/CorporateCreditsTab";
import LeasingsTab from "../components/admin/LeasingsTab";
import UslugiSiecioweTab from "../components/admin/UslugiSiecioweTab";
import DocumentsTab from "../components/admin/DocumentsTab";
import UroTab from "../components/admin/UroTab";
import PojazdyTab from "../components/admin/PojazdyTab";
import UrlowyTab from "../components/shared/UrlowyTab";
import NotificationsTab from "../components/admin/NotificationsTab";
import DochodFirmyTab from "../components/admin/DochodFirmyTab";
import KsefTab from "../components/admin/KsefTab";
import AuditLogTab from "../components/admin/AuditLogTab";
import PozyczkiPracownikowTab from "../components/admin/PozyczkiPracownikowTab";
import WorkHoursTab from "../components/admin/WorkHoursTab";
import UrzadzeniaTab from "../components/admin/UrzadzeniaTab";
import KontaktyTab from "../components/shared/KontaktyTab";
import RejonyTab from "../components/shared/RejonyTab";
import PrzypomnieniaTab from "../components/shared/PrzypomnieniaTab";
import DyzuryTab from "../components/shared/DyzuryTab";
import SettingsTab from "../components/admin/SettingsTab";
import BackupTab from "../components/admin/BackupTab";
import PocztaTab from "../components/admin/PocztaTab";
import DniowkiTab from "../components/shared/DniowkiTab";
import SalaryConfigSection, { SalaryModeBadge } from "../components/shared/SalaryConfigSection";
import DashboardHome from "../components/admin/DashboardHome";
import NotificationBell from "../components/NotificationBell";
import { ThemeToggle } from "../components/shared/ThemeToggle";
import PushBell from "../components/shared/PushBell";
import RefreshButton from "../components/shared/RefreshButton";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import ChatTab from "../components/shared/ChatTab";
import { useChatBadge } from "../contexts/ChatBadgeContext";

// Always use current origin for API calls (works for any domain)
const API = `${window.location.origin}/api`;

// Highlights a matched fragment (case-insensitive) inside a plain string.
// Returns a React fragment with <mark> tags around each match.
function highlightMatch(text, query) {
  if (!text || !query) return text || null;
  const src = String(text);
  const q = String(query).trim();
  if (!q) return src;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
  const parts = src.split(re);
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-foreground rounded px-0.5">{p}</mark>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

const MONTHS = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const ALL_FIELDS = [
  {
    key: "przychod_vat_brutto",
    label: "Przychód VAT Brutto",
    editable: true,
    type: "number",
  },
  {
    key: "doplata_paliwowa",
    label: "Dopłata paliwowa",
    editable: true,
    type: "number",
  },
  {
    key: "przychod",
    label: "Przychód",
    editable: false,
    formula: true,
    type: "number",
  },
  { key: "nota", label: "Nota", editable: true, type: "number" },
  { key: "rata", label: "Rata", editable: true, type: "number", hasNote: true },
  { key: "paliwo", label: "Paliwo (base)", editable: true, type: "number" },
  {
    key: "paliwo_total",
    label: "Paliwo razem",
    editable: false,
    computed: true,
    type: "number",
  },
  { key: "premia", label: "Premia", editable: true, type: "number" },
  {
    key: "dochod",
    label: "Dochód",
    editable: false,
    formula: true,
    type: "number",
  },
  {
    key: "przelew",
    label: "Przelew",
    editable: false,
    formula: true,
    type: "number",
  },
];

// Formatuje YYYY-MM-DD → DD.MM.YYYY
const _fmtDatePL = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

// Formuły расчёта
// mode: "percent" | "rate" | "rate_percent" | "dniowki"
//   - "rate" (stała stawka): dochod = wynagrodzenie_stale + premia (no przychod)
//   - other modes: dochod = przychod/2 + premia (legacy 50/50)
const calculateFormulas = (form, mode = "percent") => {
  const przychod_vat_brutto = parseFloat(form.przychod_vat_brutto) || 0;
  const doplata_paliwowa = parseFloat(form.doplata_paliwowa) || 0;
  const nota = parseFloat(form.nota) || 0;
  const rata = parseFloat(form.rata) || 0;
  const paliwo = parseFloat(form.paliwo) || 0;
  const paliwoItemsSum = (form.paliwo_items || []).reduce(
    (s, v) => s + (parseFloat(v) || 0),
    0
  );
  const premia = parseFloat(form.premia) || 0;
  const wyn_stale = parseFloat(form.wynagrodzenie_stale) || 0;

  // Sum all custom deductions
  const customDeductionsTotal = (form.custom_deductions || []).reduce(
    (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
    0
  );

  let przychod, dochod;
  if (mode === "rate") {
    przychod = 0;
    dochod = wyn_stale + premia;
  } else {
    przychod = przychod_vat_brutto - doplata_paliwowa;
    dochod = przychod / 2 + premia;
  }
  const przelew = dochod - paliwo - paliwoItemsSum - nota - rata - customDeductionsTotal;

  return {
    przychod: Math.round(przychod * 100) / 100,
    dochod: Math.round(dochod * 100) / 100,
    przelew: Math.round(przelew * 100) / 100,
  };
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { unread: chatUnread } = useChatBadge();
  // Init from URL ?tab=X so right-click → "open in new tab" preserves selection.
  const _initialTab = (() => {
    try { return new URLSearchParams(window.location.search).get("tab") || "home"; }
    catch { return "home"; }
  })();
  const [tab, _setTab] = useState(_initialTab);
  // Wrapper that also syncs URL. `replace` avoids polluting browser history.
  const setTab = React.useCallback((next) => {
    _setTab(next);
    try {
      const url = new URL(window.location.href);
      if (next === "home") url.searchParams.delete("tab");
      else url.searchParams.set("tab", next);
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
  }, []);
  const [obligationsSubTab, setObligationsSubTab] = useState("private-credits");
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [statsRecords, setStatsRecords] = useState([]); // all records of the year (no month filter) for SalaryStatistics
  const [loading, setLoading] = useState(true);
  // "New month" starts effectively at day 16 (mid-month cutover):
  // — Before day 16, we're still processing the previous month's second period (16-30),
  //   so default month = previous month.
  // — From day 16 onwards, switch to the current month.
  const _now = new Date();
  const _defaultMonthShift = _now.getDate() < 16 ? -1 : 0;
  const _defaultDate = new Date(_now.getFullYear(), _now.getMonth() + _defaultMonthShift, 1);
  const [selectedYear, setSelectedYear] = useState(_defaultDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(_defaultDate.getMonth() + 1);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Modal states
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingSalary, setEditingSalary] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try { return localStorage.getItem("sidebar_expanded") === "true"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_expanded", sidebarExpanded ? "true" : "false"); } catch { /* ignore */ }
  }, [sidebarExpanded]);

  // Debounced global search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/dashboard/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.data?.results || []);
        setShowSearchResults(true);
      } catch (e) {
        console.error("Search failed", e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Credits & Leasings states
  const [privateCredits, setPrivateCredits] = useState([]);
  const [corporateCredits, setCorporateCredits] = useState([]);
  const [leasings, setLeasings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [pojazdy, setPojazdy] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [creditType, setCreditType] = useState(null);
  const [creditForm, setCreditForm] = useState({
    name: "",
    employee_id: "",
    payments: [],
    polisa_payments: [],
    show_polisa: false,
    add_to_salary: false,
    salary_period: "01-15",
    auto_sync_koszty: true,
    pojazd_id: "",
  });
  const [showAutoCalc, setShowAutoCalc] = useState(false);
  const [uploadingHarmonogram, setUploadingHarmonogram] = useState(false);
  const harmonogramInputRef = React.useRef(null);
  const polisaHarmonogramInputRef = React.useRef(null);
  const [autoCalcForm, setAutoCalcForm] = useState({
    total_amount: "",
    number_of_payments: "",
    monthly_payment: "",
    payment_day: "20",
    start_month: new Date().getMonth() + 1,
    start_year: new Date().getFullYear(),
  });

  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    password: "",
    imie_nazwisko: "",
    pesel: "",
    numer_paszportu: "",
    numer_prawa_jazdy: "",
    numer_blankietu_prawa_jazdy: "",
    adres: "",
    numer_telefonu: "",
    numer_telefonu_zebra: "",
    data_urodzenia: "",
    salary_config: { mode: "percent", percent: 50, rate: 0, daily_rate: 0 },
  });
  const [salaryForm, setSalaryForm] = useState({
    employee_id: "",
    period_start: "01",
    period_end: "15",
    month: 1,
    year: 2026,
    przychod_vat_brutto: 0,
    doplata_paliwowa: 0,
    przychod: 0,
    nota: 0,
    rata_info: "",
    rata: 0,
    paliwo: 0,
    paliwo_items: [],
    premia: 0,
    dochod: 0,
    przelew: 0,
    wynagrodzenie_stale: 0,  // For "rate" mode employees (stała stawka)
    custom_deductions: [],  // [{ name: "pożyczka", amount: 500 }]
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [appliedPaliwoDraftIds, setAppliedPaliwoDraftIds] = useState([]);
  const [appliedRataRefs, setAppliedRataRefs] = useState([]);
  const [appliedPozyczkaDraftIds, setAppliedPozyczkaDraftIds] = useState([]);
  const [appliedUslugiIds, setAppliedUslugiIds] = useState([]);
  const [suggestApplied, setSuggestApplied] = useState({ paliwo: false, rata: false, pozyczka: false, uslugi: false });
  const [suggestPreview, setSuggestPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Effective salary_config mode for the currently-selected salary form.
  // Priority: existing record snapshot > employee's current config > "percent" default.
  const effectiveSalaryMode = React.useMemo(() => {
    if (editingSalary?.salary_config_snapshot?.mode) {
      return editingSalary.salary_config_snapshot.mode;
    }
    const emp = employees.find((e) => e._id === salaryForm.employee_id);
    return emp?.salary_config?.mode || "percent";
  }, [editingSalary, employees, salaryForm.employee_id]);
  const isFixedRateMode = effectiveSalaryMode === "rate";

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (tab === "salary") {
      fetchRecords();
    } else if (tab === "obligations") {
      if (obligationsSubTab === "private-credits") fetchPrivateCredits();
      else if (obligationsSubTab === "corporate-credits") fetchCorporateCredits();
      else if (obligationsSubTab === "leasings") fetchLeasings();
    } else if (tab === "documents") {
      fetchDocuments();
    }
  }, [tab, obligationsSubTab, selectedYear, selectedMonth, selectedPeriod, selectedEmployee]);

  // Auto-refresh: keep the current tab's data fresh in a long-lived PWA tab.
  // Fires on interval, visibility change, focus, and the manual refresh button.
  useAutoRefresh(() => {
    if (tab === "salary") fetchRecords();
    else if (tab === "obligations") {
      if (obligationsSubTab === "private-credits") fetchPrivateCredits();
      else if (obligationsSubTab === "corporate-credits") fetchCorporateCredits();
      else if (obligationsSubTab === "leasings") fetchLeasings();
    } else if (tab === "documents") fetchDocuments();
  });

  useEffect(() => {
    if (user?.password_change_required) {
      setShowChangePassword(true);
    }
  }, [user]);

  // Auto-fetch suggest preview AND auto-apply when modal opens.
  // Users no longer have to click "Auto-uzupełnij z bazy" — every new/unapplied
  // fuel/rata/pożyczka/usługa for the period lands as a Potrącenie automatically.
  useEffect(() => {
    if (!showSalaryModal || !salaryForm.employee_id) {
      setSuggestPreview(null);
      return;
    }
    // When employee/period changes on a NEW salary entry, wipe previously
    // auto-populated deductions so we don't mix them across employees.
    // Manual user-added deductions (no `source` and not `locked`) are kept.
    if (!editingSalary) {
      const AUTO_SOURCES = new Set(["paliwo", "rata_kredyt", "pozyczka", "usluga"]);
      setSalaryForm((prev) => {
        const kept = (prev.custom_deductions || []).filter(
          (d) => !AUTO_SOURCES.has(d.source) && !d.locked
        );
        if (kept.length === (prev.custom_deductions || []).length) return prev;
        const updated = { ...prev, custom_deductions: kept };
        return { ...updated, ...calculateFormulas(updated, effectiveSalaryMode) };
      });
      setAppliedPaliwoDraftIds([]);
      setAppliedRataRefs([]);
      setAppliedPozyczkaDraftIds([]);
      setAppliedUslugiIds([]);
      setSuggestApplied({ paliwo: false, rata: false, pozyczka: false, uslugi: false });
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await axios.get(`${API}/salary/suggest`, {
          params: {
            employee_id: salaryForm.employee_id,
            year: salaryForm.year,
            month: salaryForm.month,
            period: `${salaryForm.period_start}-${salaryForm.period_end}`,
          },
          signal: ctrl.signal,
        });
        setSuggestPreview(data);
        // Auto-apply everything not yet applied on THIS modal session.
        setSalaryForm((prev) => {
          const updated = { ...prev, custom_deductions: [...(prev.custom_deductions || [])] };
          const existingKeys = new Set(
            updated.custom_deductions.map((d) => `${d.source || ""}:${d.ref_id || ""}:${(d.ref?.credit_id || "")}:${(d.ref?.payment_number ?? "")}`)
          );
          const pushIfNew = (obj) => {
            const key = `${obj.source || ""}:${obj.ref_id || ""}:${(obj.ref?.credit_id || "")}:${(obj.ref?.payment_number ?? "")}`;
            if (existingKeys.has(key)) return;
            existingKeys.add(key);
            updated.custom_deductions.push(obj);
          };
          // Paliwo
          const paliwoPieces = data.paliwo_pieces && data.paliwo_pieces.length
            ? data.paliwo_pieces
            : (data.paliwo_items || []).map((amount, i) => ({ id: (data.paliwo_draft_ids || [])[i], amount, data: "" }));
          paliwoPieces.forEach((p, i) => {
            const amt = Number(p.amount ?? p.kwota ?? 0);
            if (!amt) return;
            const dateStr = _fmtDatePL(p.data);
            const noteStr = p.note ? ` · ${p.note}` : "";
            const label = dateStr ? `Paliwo — tankowanie z dnia ${dateStr}${noteStr}` : `Paliwo — pozycja #${i + 1}${noteStr}`;
            pushIfNew({ name: label, amount: amt, source: "paliwo", ref_id: p.id || null, locked: true });
          });
          // Rata
          (data.rata_pieces || []).forEach((p) => {
            const amt = Number(p.amount || 0);
            if (!amt) return;
            const cred = p.credit_name || "Kredyt";
            const num = p.payment_number ? ` #${p.payment_number}` : "";
            const dateStr = p.date ? ` — ${p.date}` : "";
            pushIfNew({
              name: `Rata ${cred}${num}${dateStr}`,
              amount: amt,
              source: "rata_kredyt",
              ref: { credit_id: p.credit_id, payment_number: p.payment_number },
              locked: true,
            });
          });
          // Pożyczka
          (data.pozyczka_pieces || []).forEach((p) => {
            const amt = Number(p.kwota || p.amount || 0);
            if (!amt) return;
            const dateStr = p.data ? ` — ${p.data}` : "";
            const noteStr = p.note ? ` · ${p.note}` : "";
            pushIfNew({
              name: `Pożyczka${dateStr}${noteStr}`.trim(),
              amount: amt,
              source: "pozyczka",
              ref_id: p.draft_id || p.id || null,
              locked: true,
            });
          });
          // Usługi
          (data.uslugi_pieces || []).forEach((p) => {
            const amt = Number(p.kwota || p.amount || 0);
            if (!amt) return;
            pushIfNew({
              name: `Usługa: ${p.name || ""}`.trim(),
              amount: amt,
              source: "usluga",
              ref_id: p.usluga_id || null,
              locked: true,
            });
          });
          const calculated = calculateFormulas(updated, effectiveSalaryMode);
          return { ...updated, ...calculated };
        });
        // Also update the tracking arrays used at save time.
        if ((data.paliwo_draft_ids || []).length) {
          setAppliedPaliwoDraftIds((prev) => [
            ...prev,
            ...data.paliwo_draft_ids.filter((id) => !prev.includes(id)),
          ]);
        }
        if ((data.rata_pieces || []).length) {
          const newRefs = data.rata_pieces
            .filter((p) => p.credit_id && p.payment_number != null)
            .map((p) => ({ credit_id: p.credit_id, payment_number: p.payment_number }));
          setAppliedRataRefs((prev) => {
            const key = (r) => `${r.credit_id}:${r.payment_number}`;
            const have = new Set(prev.map(key));
            return [...prev, ...newRefs.filter((r) => !have.has(key(r)))];
          });
        }
        if ((data.pozyczka_draft_ids || []).length) {
          setAppliedPozyczkaDraftIds((prev) => [
            ...prev,
            ...data.pozyczka_draft_ids.filter((id) => !prev.includes(id)),
          ]);
        }
        if ((data.uslugi_pieces || []).length) {
          const ids = data.uslugi_pieces.map((p) => p.usluga_id).filter(Boolean);
          setAppliedUslugiIds((prev) => [
            ...prev,
            ...ids.filter((id) => !prev.includes(id)),
          ]);
        }
        setSuggestApplied({ paliwo: true, rata: true, pozyczka: true, uslugi: true });
      } catch (err) {
        if (!axios.isCancel?.(err) && err.name !== "CanceledError") {
          console.error("Preview fetch error:", err);
          setSuggestPreview(null);
        }
      } finally {
        setPreviewLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showSalaryModal,
    salaryForm.employee_id,
    salaryForm.year,
    salaryForm.month,
    salaryForm.period_start,
    salaryForm.period_end,
  ]);

  // When employee changes in "Nowy wpis" (not edit), prefill wynagrodzenie_stale
  // from their salary_config.rate if they're in fixed-rate mode.
  useEffect(() => {
    if (!showSalaryModal || editingSalary || !salaryForm.employee_id) return;
    const emp = employees.find((e) => e._id === salaryForm.employee_id);
    if (!emp) return;
    const mode = emp.salary_config?.mode || "percent";
    const rate = mode === "rate" ? Number(emp.salary_config?.rate || 0) : 0;
    setSalaryForm((prev) => {
      const updated = { ...prev, wynagrodzenie_stale: rate };
      return { ...updated, ...calculateFormulas(updated, mode) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSalaryModal, editingSalary, salaryForm.employee_id, employees]);

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(`${API}/employees`);
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      if (selectedPeriod) {
        const [start, end] = selectedPeriod.split("-");
        params.period_start = start;
        params.period_end = end;
      }
      if (selectedEmployee) params.employee_id = selectedEmployee;
      const { data } = await axios.get(`${API}/salary/all`, { params });
      setRecords(data);

      // Stats: fetch wider window (current year + previous year if January is selected)
      // Always ignore month filter so previous-month % can compute even when filter is on.
      try {
        const yearsToFetch = selectedMonth === 1
          ? [selectedYear, selectedYear - 1]
          : [selectedYear];
        const all = (
          await Promise.all(
            yearsToFetch.map((y) =>
              axios.get(`${API}/salary/all`, { params: { year: y } }).then((r) => r.data)
            )
          )
        ).flat();
        setStatsRecords(all);
      } catch (statsErr) {
        console.error("Stats fetch failed:", statsErr);
        setStatsRecords(data); // fallback to filtered records
      }
    } catch (err) {
      console.error("Error fetching records:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivateCredits = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/private-credits`);
      setPrivateCredits(data);
    } catch (err) {
      console.error("Error fetching private credits:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCorporateCredits = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/corporate-credits`);
      setCorporateCredits(data);
    } catch (err) {
      console.error("Error fetching corporate credits:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeasings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leasings`);
      setLeasings(data);
    } catch (err) {
      console.error("Error fetching leasings:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPojazdy = async () => {
    try {
      const { data } = await axios.get(`${API}/pojazdy`);
      setPojazdy(data || []);
    } catch (err) {
      console.error("Error fetching pojazdy:", err);
      setPojazdy([]);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents`);
      setDocuments(data);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (documentId) => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/documents/${documentId}?token=${token}`, "_blank");
  };

  // Credits CRUD
  const openAddCredit = (type) => {
    setCreditType(type);
    setEditingCredit(null);
    setCreditForm({
      name: "",
      employee_id: employees.length > 0 ? employees[0]._id : "",
      payments: [
        { payment_number: 1, date: "", amount: 0, remaining: 0, paid: false },
      ],
      polisa_payments: [],
      show_polisa: false,
      add_to_salary: false,
      salary_period: "01-15",
      auto_sync_koszty: true,
      pojazd_id: "",
    });
    if (type === "leasing") {
      fetchPojazdy();
    }
    setShowCreditModal(true);
  };

  const openEditCredit = (credit, type) => {
    setCreditType(type);
    setEditingCredit(credit);
    const polisa = Array.isArray(credit.polisa_payments)
      ? credit.polisa_payments
      : [];
    setCreditForm({
      name: credit.name,
      employee_id: credit.employee_id || "",
      payments: credit.payments || [],
      polisa_payments: polisa,
      show_polisa: polisa.length > 0,
      add_to_salary: credit.add_to_salary === true,
      salary_period: credit.salary_period || "01-15",
      auto_sync_koszty: credit.auto_sync_koszty !== false,
      pojazd_id: credit.pojazd_id || "",
    });
    if (type === "leasing") {
      fetchPojazdy();
    }
    setShowCreditModal(true);
  };

  const handleSaveCredit = async () => {
    setSaving(true);
    try {
      const endpoint =
        creditType === "private"
          ? "private-credits"
          : creditType === "corporate"
            ? "corporate-credits"
            : "leasings";

      const payload = {
        name: creditForm.name,
        payments: creditForm.payments,
        add_to_salary: creditForm.add_to_salary || false,
        salary_period: creditForm.salary_period,
      };

      if (creditType === "private") {
        payload.employee_id = creditForm.employee_id;
      }
      if (creditType === "corporate") {
        payload.auto_sync_koszty = !!creditForm.auto_sync_koszty;
      }
      if (creditType === "leasing") {
        payload.pojazd_id = creditForm.pojazd_id || null;
        payload.auto_sync_koszty = !!creditForm.auto_sync_koszty;
        payload.polisa_payments = creditForm.polisa_payments || [];
      }

      if (editingCredit) {
        await axios.put(`${API}/${endpoint}/${editingCredit.id}`, payload);
      } else {
        await axios.post(`${API}/${endpoint}`, payload);
      }

      setShowCreditModal(false);
      setEditingCredit(null);
      setCreditForm({
        name: "",
        employee_id: "",
        payments: [],
        polisa_payments: [],
        show_polisa: false,
        add_to_salary: false,
        salary_period: "01-15",
        auto_sync_koszty: true,
        pojazd_id: "",
      });

      if (creditType === "private") fetchPrivateCredits();
      else if (creditType === "corporate") fetchCorporateCredits();
      else fetchLeasings();
    } catch (err) {
      console.error("Error saving credit:", err);
      alert(
        "Błąd podczas zapisywania: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredit = async (creditId, type) => {
    if (!window.confirm("Czy na pewno chcesz usunąć?")) return;

    try {
      const endpoint =
        type === "private"
          ? "private-credits"
          : type === "corporate"
            ? "corporate-credits"
            : "leasings";

      await axios.delete(`${API}/${endpoint}/${creditId}`);

      if (type === "private") fetchPrivateCredits();
      else if (type === "corporate") fetchCorporateCredits();
      else fetchLeasings();
    } catch (err) {
      console.error("Error deleting credit:", err);
      alert(
        "Błąd podczas usuwania: " + (err.response?.data?.detail || err.message),
      );
    }
  };

  const addPayment = () => {
    const newPaymentNumber = creditForm.payments.length + 1;
    setCreditForm((prev) => ({
      ...prev,
      payments: [
        ...prev.payments,
        {
          payment_number: newPaymentNumber,
          date: "",
          amount: "",
          remaining: "",
          odsetki: "",
          paid: false,
        },
      ],
    }));
  };

  const removePayment = (index) => {
    setCreditForm((prev) => ({
      ...prev,
      payments: prev.payments.filter((_, i) => i !== index),
    }));
  };

  const updatePayment = (index, field, value) => {
    setCreditForm((prev) => {
      const updated = [...prev.payments];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, payments: updated };
    });
  };

  // Apply 23% VAT to all installments (rata, kapital, odsetki, remaining)
  const applyVATToPayments = () => {
    if (!creditForm.payments.length) {
      alert("Brak rat do modyfikacji.");
      return;
    }
    if (
      !window.confirm(
        "Zastosować +23% VAT do wszystkich rat (kwota, odsetki, kapitał, pozostało)?",
      )
    ) {
      return;
    }
    const VAT = 1.23;
    setCreditForm((prev) => ({
      ...prev,
      payments: prev.payments.map((p) => ({
        ...p,
        amount:
          p.amount !== "" && p.amount != null
            ? Math.round(Number(p.amount) * VAT * 100) / 100
            : p.amount,
        odsetki:
          p.odsetki !== "" && p.odsetki != null
            ? Math.round(Number(p.odsetki) * VAT * 100) / 100
            : p.odsetki,
        kapital:
          p.kapital !== "" && p.kapital != null
            ? Math.round(Number(p.kapital) * VAT * 100) / 100
            : p.kapital,
        remaining:
          p.remaining !== "" && p.remaining != null
            ? Math.round(Number(p.remaining) * VAT * 100) / 100
            : p.remaining,
      })),
    }));
  };

  // Polisa (insurance) installments handlers
  const togglePolisaSection = () => {
    setCreditForm((prev) => {
      const next = !prev.show_polisa;
      return {
        ...prev,
        show_polisa: next,
        polisa_payments:
          next && prev.polisa_payments.length === 0
            ? [
                {
                  payment_number: 1,
                  date: "",
                  amount: 0,
                  remaining: 0,
                  paid: false,
                },
              ]
            : prev.polisa_payments,
      };
    });
  };

  const addPolisaPayment = () => {
    setCreditForm((prev) => ({
      ...prev,
      polisa_payments: [
        ...prev.polisa_payments,
        {
          payment_number: prev.polisa_payments.length + 1,
          date: "",
          amount: 0,
          remaining: 0,
          paid: false,
        },
      ],
    }));
  };

  const removePolisaPayment = (index) => {
    setCreditForm((prev) => ({
      ...prev,
      polisa_payments: prev.polisa_payments.filter((_, i) => i !== index),
    }));
  };

  const updatePolisaPayment = (index, field, value) => {
    setCreditForm((prev) => {
      const updated = [...prev.polisa_payments];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, polisa_payments: updated };
    });
  };

  const generatePayments = () => {
    const {
      total_amount,
      number_of_payments,
      monthly_payment,
      payment_day,
      start_month,
      start_year,
    } = autoCalcForm;

    if (!total_amount || !number_of_payments || !monthly_payment) {
      alert(
        "Wypełnij wszystkie pola: suma kredytu, liczba rat, rata miesięczna",
      );
      return;
    }

    const payments = [];
    let remaining = parseFloat(total_amount);
    const payment = parseFloat(monthly_payment);
    const numPayments = parseInt(number_of_payments);

    let currentMonth = parseInt(start_month);
    let currentYear = parseInt(start_year);

    for (let i = 1; i <= numPayments; i++) {
      const amount = i === numPayments ? remaining : payment;
      remaining = Math.max(0, remaining - payment);

      const date = `${payment_day}.${String(currentMonth).padStart(2, "0")}.${currentYear}`;

      payments.push({
        payment_number: i,
        date: date,
        amount: parseFloat(amount.toFixed(2)),
        remaining: parseFloat(remaining.toFixed(2)),
        paid: false,
      });

      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    setCreditForm((prev) => ({
      ...prev,
      payments: payments,
    }));

    setShowAutoCalc(false);
    alert(`Wygenerowano ${numPayments} rat!`);
  };

  const handleUploadHarmonogram = async (file, target = "payments") => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const allowedExt = [".pdf", ".csv", ".xlsx", ".xls"];
    const isAllowed =
      file.type === "application/pdf" ||
      allowedExt.some((ext) => name.endsWith(ext));
    if (!isAllowed) {
      alert("Dozwolone formaty: PDF, CSV, XLSX.");
      return;
    }
    setUploadingHarmonogram(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/corporate-credits/parse-harmonogram`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      const payments = (data.payments || []).map((p, i) => ({
        payment_number: i + 1,
        date: p.date,
        amount: p.amount,
        remaining: p.remaining,
        odsetki: p.odsetki ?? null,
        kapital: p.kapital ?? null,
        paid: false,
      }));
      if (payments.length === 0) {
        alert("Nie rozpoznano żadnych rat w harmonogramie.");
        return;
      }
      setCreditForm((prev) => ({ ...prev, [target]: payments }));
      setShowAutoCalc(false);
      const label = target === "polisa_payments" ? "polisy" : "rat";
      const sourceNote =
        data.source === "ai-vision"
          ? "\n\n⚠️ Tekst nie był czytelny — użyto AI (vision). Proszę sprawdź wartości."
          : data.source === "ai-text"
            ? "\n\n⚠️ Regex zawiódł — użyto AI (text). Proszę sprawdź wartości."
            : data.source?.startsWith("tabular-")
              ? `\n\n✓ Źródło: ${data.source.replace("tabular-", "").toUpperCase()}`
              : "";
      alert(
        `Wgrano harmonogram ${label}: ${payments.length} rat.\nSuma: ${data.total_amount?.toFixed(2)} PLN\nKapitał: ${data.total_kapital?.toFixed(2)} PLN\nOdsetki: ${data.total_odsetki?.toFixed(2)} PLN${sourceNote}`,
      );
    } catch (err) {
      alert(
        "Błąd wgrywania harmonogramu: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setUploadingHarmonogram(false);
      if (harmonogramInputRef.current) {
        harmonogramInputRef.current.value = "";
      }
    }
  };

  // Employee CRUD
  const handleSaveEmployee = async () => {
    setSaving(true);
    try {
      let empId;
      if (editingEmployee) {
        const updateData = {
          name: employeeForm.name,
          email: employeeForm.email,
          imie_nazwisko: employeeForm.imie_nazwisko || null,
          pesel: employeeForm.pesel || null,
          numer_paszportu: employeeForm.numer_paszportu || null,
          numer_prawa_jazdy: employeeForm.numer_prawa_jazdy || null,
          numer_blankietu_prawa_jazdy: employeeForm.numer_blankietu_prawa_jazdy || null,
          adres: employeeForm.adres || null,
          numer_telefonu: employeeForm.numer_telefonu || null,
          numer_telefonu_zebra: employeeForm.numer_telefonu_zebra || null,
          data_urodzenia: employeeForm.data_urodzenia || null,
        };
        if (employeeForm.password) updateData.password = employeeForm.password;
        await axios.put(`${API}/employees/${editingEmployee._id}`, updateData);
        empId = editingEmployee._id;
      } else {
        const { data } = await axios.post(`${API}/employees`, employeeForm);
        empId = data?._id;
      }
      // Always persist salary_config alongside (works for both create + edit).
      if (empId && employeeForm.salary_config) {
        try {
          await axios.put(`${API}/employees/${empId}/salary-config`, employeeForm.salary_config);
        } catch (cfgErr) {
          console.warn("salary_config save failed", cfgErr);
          alert("Pracownik zapisany, ale zapis systemu wynagrodzenia nie powiódł się: " + (cfgErr.response?.data?.detail || cfgErr.message));
        }
      }
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      setEmployeeForm({
        name: "", email: "", password: "", imie_nazwisko: "",
        pesel: "", numer_paszportu: "", numer_prawa_jazdy: "",
        numer_blankietu_prawa_jazdy: "", adres: "", numer_telefonu: "",
        numer_telefonu_zebra: "", data_urodzenia: "",
        salary_config: { mode: "percent", percent: 50, rate: 0, daily_rate: 0 },
      });
      fetchEmployees();
    } catch (err) {
      console.error("Error saving employee:", err);
      alert(err.response?.data?.detail || "Error saving employee");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      await axios.delete(`${API}/employees/${id}`);
      fetchEmployees();
    } catch (err) {
      console.error("Error deleting employee:", err);
      alert("Ошибка: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleResetPassword = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/employees/${id}/reset-password`,
        {},
      );
      setNewPassword(data.new_password);
    } catch (err) {
      console.error("Error resetting password:", err);
    }
  };

  // Salary CRUD
  const applySuggestion = async (kind) => {
    if (!salaryForm.employee_id) {
      alert("Najpierw wybierz pracownika");
      return;
    }
    const wantPaliwo = kind === "paliwo" || kind === "both";
    const wantRata = kind === "rata" || kind === "both";
    const wantPozyczka = kind === "pozyczka" || kind === "both";
    const wantUslugi = kind === "uslugi" || kind === "both";
    if (kind === "paliwo" && suggestApplied.paliwo) { alert("Paliwo zostało już dodane"); return; }
    if (kind === "rata" && suggestApplied.rata) { alert("Rata została już dodana"); return; }
    if (kind === "pozyczka" && suggestApplied.pozyczka) { alert("Pożyczki zostały już dodane"); return; }
    if (kind === "uslugi" && suggestApplied.uslugi) { alert("Usługi już dodane"); return; }
    setSuggesting(true);
    try {
      const { data } = await axios.get(`${API}/salary/suggest`, {
        params: {
          employee_id: salaryForm.employee_id,
          year: salaryForm.year,
          month: salaryForm.month,
          period: `${salaryForm.period_start}-${salaryForm.period_end}`,
        },
      });
      const applyPaliwo = wantPaliwo && !suggestApplied.paliwo;
      const applyRata = wantRata && !suggestApplied.rata;
      const applyPozyczka = wantPozyczka && !suggestApplied.pozyczka;
      const applyUslugi = wantUslugi && !suggestApplied.uslugi;
      setSalaryForm((prev) => {
        const updated = { ...prev };
        const newDeductions = [];
        if (applyPaliwo) {
          // Push each fuel piece as a locked line item in Potrącenia.
          // Fallback to indexed name if we lack date info (older backend).
          const pieces = data.paliwo_pieces && data.paliwo_pieces.length
            ? data.paliwo_pieces
            : (data.paliwo_items || []).map((amount, i) => ({ id: (data.paliwo_draft_ids || [])[i], amount, data: "" }));
          pieces.forEach((p, i) => {
            const amt = Number(p.amount ?? p.kwota ?? 0);
            if (!amt) return;
            const dateStr = _fmtDatePL(p.data);
            const noteStr = p.note ? ` · ${p.note}` : "";
            const label = dateStr ? `Paliwo — tankowanie z dnia ${dateStr}${noteStr}` : `Paliwo — pozycja #${i + 1}${noteStr}`;
            newDeductions.push({
              name: label,
              amount: amt,
              source: "paliwo",
              ref_id: p.id || null,
              locked: true,
            });
          });
        }
        if (applyRata) {
          (data.rata_pieces || []).forEach((p) => {
            const amt = Number(p.amount || 0);
            if (!amt) return;
            const cred = p.credit_name || "Kredyt";
            const num = p.payment_number ? ` #${p.payment_number}` : "";
            const dateStr = p.date ? ` — ${p.date}` : "";
            newDeductions.push({
              name: `Rata ${cred}${num}${dateStr}`,
              amount: amt,
              source: "rata_kredyt",
              ref: { credit_id: p.credit_id, payment_number: p.payment_number },
              locked: true,
            });
          });
        }
        if (applyPozyczka && (data.pozyczka_pieces || []).length) {
          (data.pozyczka_pieces || []).forEach((p) => {
            const amt = Number(p.kwota || p.amount || 0);
            if (!amt) return;
            const dateStr = p.data ? ` — ${p.data}` : "";
            const noteStr = p.note ? ` · ${p.note}` : "";
            newDeductions.push({
              name: `Pożyczka${dateStr}${noteStr}`.trim(),
              amount: amt,
              source: "pozyczka",
              ref_id: p.draft_id || p.id || null,
              locked: true,
            });
          });
        }
        if (applyUslugi && (data.uslugi_pieces || []).length) {
          (data.uslugi_pieces || []).forEach((p) => {
            const amt = Number(p.kwota || p.amount || 0);
            if (!amt) return;
            newDeductions.push({
              name: `Usługa: ${p.name || ""}`.trim(),
              amount: amt,
              source: "usluga",
              ref_id: p.usluga_id || null,
              locked: true,
            });
          });
        }
        if (newDeductions.length) {
          updated.custom_deductions = [
            ...(prev.custom_deductions || []),
            ...newDeductions,
          ];
        }
        const calculated = calculateFormulas(updated, effectiveSalaryMode);
        return { ...updated, ...calculated };
      });
      if (applyPaliwo && (data.paliwo_draft_ids || []).length) {
        setAppliedPaliwoDraftIds((prev) => [
          ...prev,
          ...data.paliwo_draft_ids.filter((id) => !prev.includes(id)),
        ]);
      }
      if (applyRata && (data.rata_pieces || []).length) {
        const newRefs = data.rata_pieces
          .filter((p) => p.credit_id && p.payment_number != null)
          .map((p) => ({ credit_id: p.credit_id, payment_number: p.payment_number }));
        setAppliedRataRefs((prev) => {
          const key = (r) => `${r.credit_id}:${r.payment_number}`;
          const have = new Set(prev.map(key));
          return [...prev, ...newRefs.filter((r) => !have.has(key(r)))];
        });
      }
      if (applyPozyczka && (data.pozyczka_draft_ids || []).length) {
        setAppliedPozyczkaDraftIds((prev) => [
          ...prev,
          ...data.pozyczka_draft_ids.filter((id) => !prev.includes(id)),
        ]);
      }
      if (applyUslugi && (data.uslugi_pieces || []).length) {
        const ids = data.uslugi_pieces.map(p => p.usluga_id).filter(Boolean);
        setAppliedUslugiIds((prev) => [
          ...prev,
          ...ids.filter((id) => !prev.includes(id)),
        ]);
      }
      setSuggestApplied((prev) => ({
        paliwo: prev.paliwo || applyPaliwo,
        rata: prev.rata || applyRata,
        pozyczka: prev.pozyczka || applyPozyczka,
        uslugi: prev.uslugi || applyUslugi,
      }));
      const nothing =
        (!applyPaliwo || !data.paliwo) &&
        (!applyRata || !data.rata) &&
        (!applyPozyczka || !data.pozyczka) &&
        (!applyUslugi || !data.uslugi);
      if (nothing) {
        alert("Brak danych do uzupełnienia za wybrany okres");
      }
    } catch (err) {
      console.error("Error fetching salary suggestion:", err);
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSuggesting(false);
    }
  };

  const handleSaveSalary = async () => {
    setSaving(true);
    try {
      const calculated = calculateFormulas(salaryForm, effectiveSalaryMode);
      // In "rate" mode we hide przychod/doplata fields — force them to 0 in payload
      const rateModePatch = isFixedRateMode
        ? { przychod_vat_brutto: 0, doplata_paliwowa: 0, przychod: 0 }
        : {};
      const dataToSend = {
        ...salaryForm,
        przychod_vat_brutto: parseFloat(salaryForm.przychod_vat_brutto) || 0,
        doplata_paliwowa: parseFloat(salaryForm.doplata_paliwowa) || 0,
        nota: parseFloat(salaryForm.nota) || 0,
        rata_info: salaryForm.rata_info || "",
        rata: parseFloat(salaryForm.rata) || 0,
        paliwo: parseFloat(salaryForm.paliwo) || 0,
        paliwo_items: salaryForm.paliwo_items || [],
        premia: parseFloat(salaryForm.premia) || 0,
        custom_deductions: salaryForm.custom_deductions || [],
        applied_paliwo_draft_ids: appliedPaliwoDraftIds,
        applied_rata_refs: appliedRataRefs,
        applied_pozyczka_draft_ids: appliedPozyczkaDraftIds,
        applied_uslugi_ids: appliedUslugiIds,
        ...calculated,
        ...rateModePatch,
      };
      // Frontend-only helper field — not part of backend SalaryRecordCreate schema
      delete dataToSend.wynagrodzenie_stale;

      if (editingSalary) {
        const updateData = { ...dataToSend };
        delete updateData.employee_id;
        delete updateData.period_start;
        delete updateData.period_end;
        delete updateData.month;
        delete updateData.year;
        await axios.put(`${API}/salary/${editingSalary.id}`, updateData);
      } else {
        await axios.post(`${API}/salary`, dataToSend);
      }
      setShowSalaryModal(false);
      setEditingSalary(null);
      setSalaryForm({
        employee_id: "",
        period_start: "01",
        period_end: "15",
        month: 1,
        year: 2026,
        przychod_vat_brutto: 0,
        doplata_paliwowa: 0,
        przychod: 0,
        nota: 0,
        rata_info: "",
        rata: 0,
        paliwo: 0,
        paliwo_items: [],
        premia: 0,
        dochod: 0,
        przelew: 0,
        wynagrodzenie_stale: 0,
        custom_deductions: [],  // Reset custom deductions
      });
      setAppliedPaliwoDraftIds([]);
      setAppliedRataRefs([]);
      setAppliedPozyczkaDraftIds([]);
      setAppliedUslugiIds([]);
      setSuggestApplied({ paliwo: false, rata: false, pozyczka: false, uslugi: false });
      fetchRecords();
    } catch (err) {
      console.error("Error saving salary:", err);
      alert(err.response?.data?.detail || "Error saving salary");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSalary = async (id) => {
    try {
      await axios.delete(`${API}/salary/${id}`);
      fetchRecords();
    } catch (err) {
      console.error("Error deleting salary:", err);
      alert("Ошибка: " + (err.response?.data?.detail || err.message));
    }
  };

  const handlePDFUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Check all files are PDFs
    const nonPdfFiles = Array.from(files).filter(file => !file.name.endsWith(".pdf"));
    if (nonPdfFiles.length > 0) {
      alert(`Tylko pliki PDF są dozwolone. Znaleziono ${nonPdfFiles.length} nieprawidłowych plików.`);
      return;
    }

    setUploadingPDF(true);
    
    const results = {
      success: [],
      errors: []
    };

    // Process files one by one
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await axios.post(`${API}/salary/upload-pdf`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        results.success.push({
          filename: file.name,
          employee: response.data.record.employee_name,
          doplataDoAuta: Number(response.data.doplata_do_auta || 0),
          doplataWarning: response.data.doplata_do_auta_warning,
        });
        
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        const errorMsg = err.response?.data?.detail || "Błąd przetwarzania";
        results.errors.push({
          filename: file.name,
          error: errorMsg
        });
      }
    }

    setUploadingPDF(false);
    event.target.value = "";

    // Show summary
    let message = "";
    if (results.success.length > 0) {
      message += `✅ Pomyślnie przetworzono ${results.success.length} plików:\n`;
      results.success.forEach(r => {
        message += `  • ${r.filename} → ${r.employee}\n`;
      });
    }
    const withDoplata = results.success.filter(r => r.doplataDoAuta > 0 || r.doplataWarning);
    if (withDoplata.length > 0) {
      message += `\n⚠️ Dopłata do auta (odjęta od wynagrodzenia) w ${withDoplata.length} plikach:\n`;
      withDoplata.forEach(r => {
        message += r.doplataDoAuta > 0
          ? `  • ${r.filename} → ${r.employee}: −${r.doplataDoAuta.toFixed(2)} zł\n`
          : `  • ${r.filename} → ${r.employee}: ${r.doplataWarning}\n`;
      });
    }
    if (results.errors.length > 0) {
      message += `\n❌ Błędy (${results.errors.length} plików):\n`;
      results.errors.forEach(r => {
        message += `  • ${r.filename}: ${r.error}\n`;
      });
    }
    
    alert(message);
    
    if (results.success.length > 0) {
      fetchRecords();
    }
  };

  // Export to PDF function
  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text("Raport Wynagrodzeń", 14, 20);
    
    // Subtitle
    doc.setFontSize(11);
    const monthNames = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", 
                        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
    const filterText = selectedMonth 
      ? `${monthNames[selectedMonth - 1]} ${selectedYear}`
      : `Rok ${selectedYear}`;
    doc.text(filterText, 14, 28);
    
    // Prepare data
    const tableData = records
      .filter(r => {
        if (selectedMonth && r.month !== selectedMonth) return false;
        if (selectedYear && r.year !== selectedYear) return false;
        return true;
      })
      .map(record => {
        const basePaliwo = record.paliwo || 0;
        const itemsSum = (record.paliwo_items || []).reduce((s, v) => s + (v || 0), 0);
        const totalPaliwo = basePaliwo + itemsSum;
        const customDeductionsTotal = (record.custom_deductions || []).reduce(
          (s, d) => s + (parseFloat(d.amount) || 0), 0
        );
        const przelew = (record.dochod || 0) - totalPaliwo - (record.nota || 0) - 
                       (parseFloat(record.rata) || 0) - customDeductionsTotal;
        
        return [
          record.employee_name,
          `${record.period_start}-${record.period_end}.${record.month}.${record.year}`,
          (record.przychod_vat_brutto || 0).toFixed(2),
          (record.dochod || 0).toFixed(2),
          (record.nota || 0).toFixed(2),
          totalPaliwo.toFixed(2),
          przelew.toFixed(2)
        ];
      });
    
    // Add table
    doc.autoTable({
      head: [['Pracownik', 'Okres', 'Przychód', 'Dochód', 'Nota', 'Paliwo', 'Przelew']],
      body: tableData,
      startY: 35,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Wygenerowano: ${new Date().toLocaleString('pl-PL')} | Strona ${i} z ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10
      );
    }
    
    // Save
    const filename = selectedMonth
      ? `raport_${monthNames[selectedMonth - 1]}_${selectedYear}.pdf`
      : `raport_${selectedYear}.pdf`;
    doc.save(filename);
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      email: emp.email,
      password: "",
      imie_nazwisko: emp.imie_nazwisko || "",
      pesel: emp.pesel || "",
      numer_paszportu: emp.numer_paszportu || "",
      numer_prawa_jazdy: emp.numer_prawa_jazdy || "",
      numer_blankietu_prawa_jazdy: emp.numer_blankietu_prawa_jazdy || "",
      adres: emp.adres || "",
      numer_telefonu: emp.numer_telefonu || "",
      numer_telefonu_zebra: emp.numer_telefonu_zebra || "",
      data_urodzenia: emp.data_urodzenia || "",
      salary_config: emp.salary_config || { mode: "percent", percent: 50, rate: 0, daily_rate: 0 },
    });
    setShowEmployeeModal(true);
  };

  const openEditSalary = (record) => {
    setEditingSalary(record);
    // For "rate" mode records: derive wynagrodzenie_stale from snapshot or from dochod-premia
    const snapMode = record.salary_config_snapshot?.mode;
    const wynStale =
      snapMode === "rate"
        ? Number(
            record.salary_config_snapshot?.rate ??
              Math.max(0, (record.dochod || 0) - (record.premia || 0))
          )
        : 0;
    // Unified "Potrącenia" list: migrate legacy paliwo_items + rata into custom_deductions
    // as locked entries (visual only — user asked for a single unified list).
    const existingDeductions = Array.isArray(record.custom_deductions) ? [...record.custom_deductions] : [];
    const hasPaliwoRow = existingDeductions.some((d) => d?.source === "paliwo");
    const hasRataRow = existingDeductions.some((d) => d?.source === "rata_kredyt");
    const migratedDeductions = [];
    if (!hasPaliwoRow) {
      const items = record.paliwo_items || [];
      items.forEach((amt, i) => {
        const v = Number(amt);
        if (v > 0) {
          migratedDeductions.push({
            name: `Paliwo — pozycja #${i + 1}`,
            amount: v,
            source: "paliwo",
            locked: true,
          });
        }
      });
      // If a legacy record has aggregated paliwo without items, add a single row.
      const itemsSum = items.reduce((s, v) => s + (Number(v) || 0), 0);
      const totalPaliwo = Number(record.paliwo) || 0;
      if (items.length === 0 && totalPaliwo > 0) {
        migratedDeductions.push({
          name: "Paliwo",
          amount: totalPaliwo,
          source: "paliwo",
          locked: true,
        });
      } else if (items.length > 0 && totalPaliwo > 0 && Math.abs(totalPaliwo - itemsSum) > 0.01) {
        // Rare edge: totalPaliwo differs from items sum — surface residual.
        migratedDeductions.push({
          name: "Paliwo — pozostała kwota",
          amount: Number((totalPaliwo - itemsSum).toFixed(2)),
          source: "paliwo",
          locked: true,
        });
      }
    }
    if (!hasRataRow) {
      const totalRata = Number(record.rata) || 0;
      // Skip the legacy migration when the record has server-side
      // applied_rata_refs — Potrącenia already renders detailed per-credit
      // rows from `rata_details` and this injection would just duplicate
      // them (and, on save, zero out `record.rata`, breaking calcs).
      const hasAppliedRefs = (record.applied_rata_refs || []).length > 0;
      if (totalRata > 0 && !hasAppliedRefs) {
        migratedDeductions.push({
          name: `Rata${record.rata_info ? ": " + record.rata_info : ""}`,
          amount: totalRata,
          source: "rata_kredyt",
          locked: true,
        });
      }
    }
    // When the record HAS applied_rata_refs (e.g. from PDF upload) but the
    // corresponding custom_deductions rows weren't persisted, synthesize them
    // from `rata_details` so the edit modal shows the raty just like the row
    // view does. Without this the admin sees a red total on the row but an
    // empty rata list in the modal — exactly the bug reported.
    const existingRataKeys = new Set(
      existingDeductions
        .filter((d) => d?.source === "rata_kredyt" && d?.ref?.credit_id)
        .map((d) => `${d.ref.credit_id}:${d.ref.payment_number ?? ""}`)
    );
    (record.rata_details || []).forEach((rd) => {
      const key = `${rd.credit_id}:${rd.payment_number ?? ""}`;
      if (existingRataKeys.has(key)) return;
      const amt = Number(rd.amount) || 0;
      if (amt <= 0) return;
      const dueSuffix = rd.due_date ? ` — ${rd.due_date}` : "";
      const numSuffix = rd.payment_number != null && rd.total_payments
        ? ` (${rd.payment_number}/${rd.total_payments})`
        : "";
      migratedDeductions.push({
        name: `Rata ${rd.credit_name}${numSuffix}${dueSuffix}`,
        amount: amt,
        source: "rata_kredyt",
        ref: { credit_id: rd.credit_id, payment_number: rd.payment_number },
        locked: true,
      });
      existingRataKeys.add(key);
    });
    setSalaryForm({
      employee_id: record.employee_id,
      period_start: record.period_start,
      period_end: record.period_end,
      month: record.month,
      year: record.year,
      przychod_vat_brutto: record.przychod_vat_brutto,
      doplata_paliwowa: record.doplata_paliwowa,
      przychod: record.przychod,
      nota: record.nota,
      rata_info: record.rata_info || record.rata_note || "",
      // Wyzerowane: paliwo/rata/paliwo_items są zmigrowane do custom_deductions.
      // Na zapisie pojadą jako 0 (backend akceptuje).
      rata: 0,
      paliwo: 0,
      paliwo_items: [],
      premia: record.premia,
      dochod: record.dochod,
      przelew: record.przelew,
      wynagrodzenie_stale: wynStale,
      custom_deductions: [...existingDeductions, ...migratedDeductions],
    });
    setAppliedPaliwoDraftIds([]);
    setAppliedRataRefs([]);
    setAppliedPozyczkaDraftIds([]);
    setAppliedUslugiIds([]);
    setSuggestApplied({ paliwo: false, rata: false, pozyczka: false, uslugi: false });
    setShowSalaryModal(true);
  };

  const openAddSalary = () => {
    setEditingSalary(null);
    const firstEmp = employees[0];
    const initialMode = firstEmp?.salary_config?.mode || "percent";
    const initialRate =
      initialMode === "rate" ? Number(firstEmp?.salary_config?.rate || 0) : 0;
    setSalaryForm({
      employee_id: firstEmp?._id || "",
      period_start: "01",
      period_end: "15",
      month: selectedMonth || 1,
      year: selectedYear,
      przychod_vat_brutto: 0,
      doplata_paliwowa: 0,
      przychod: 0,
      nota: 0,
      rata_info: "",
      rata: 0,
      paliwo: 0,
      paliwo_items: [],
      premia: 0,
      dochod: initialRate,  // preview dochod as rate for fixed-rate employees
      przelew: initialRate,
      wynagrodzenie_stale: initialRate,
      custom_deductions: [],
    });
    setAppliedPaliwoDraftIds([]);
    setAppliedRataRefs([]);
    setAppliedPozyczkaDraftIds([]);
    setAppliedUslugiIds([]);
    setSuggestApplied({ paliwo: false, rata: false, pozyczka: false, uslugi: false });
    setShowSalaryModal(true);
  };

  const NAV_GROUPS = [
    {
      label: null,
      items: [{ key: "home", label: "Start", Icon: House }],
    },
    {
      label: "Praca",
      items: [
        { key: "employees", label: "Pracownicy", Icon: Users },
        { key: "salary", label: "Wynagrodzenia", Icon: Wallet },
        { key: "urlopy", label: "Urlopy", Icon: Umbrella },
        { key: "work-hours", label: "Ewidencja godzin", Icon: Clock },
      ],
    },
    {
      label: "Finanse",
      items: [
        { key: "obligations", label: "Zobowiązania", Icon: Receipt },
        { key: "pozyczki", label: "Pożyczki", Icon: HandCoins },
        { key: "dochod", label: "Dochód firmy", Icon: ChartLineUp },
        { key: "ksef", label: "KSeF", Icon: FileText },
      ],
    },
    {
      label: "Flota",
      items: [
        { key: "pojazdy", label: "Pojazdy", Icon: Car },
        { key: "urzadzenia", label: "Urządzenia", Icon: Cpu },
      ],
    },
    {
      label: "Dokumenty i system",
      items: [
        { key: "documents", label: "Dokumenty", Icon: FilePdf },
        { key: "notifications", label: "Powiadomienia", Icon: Bell },
        { key: "backup", label: "Kopie zapasowe", Icon: ShieldCheck },
      ],
    },
    {
      label: "Inne",
      items: [
        { key: "chat", label: "Czat", Icon: ChatCircle },
        { key: "poczta", label: "Poczta", Icon: Envelope },
        { key: "kontakty", label: "Kontakty", Icon: AddressBook },
        { key: "rejony", label: "Rejony", Icon: MapPin },
        { key: "przypomnienia", label: "Przypomnienia", Icon: BellRinging },
        { key: "dyzury", label: "Dyżury", Icon: CalendarCheck },
        { key: "dniowki", label: "Dniówki", Icon: CalendarDots },
        { key: "audit", label: "Aktywność", Icon: ClockCounterClockwise },
        { key: "settings", label: "Ustawienia", Icon: Gear },
      ],
    },
  ];
  const [hiddenTabs, setHiddenTabs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        if (!cancelled) setHiddenTabs(data?.ui?.hidden_admin_tabs || []);
      } catch { /* fails-open */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items).filter((it) => !hiddenTabs.includes(it.key));

  const _periodDisplay = selectedPeriod === "1-15"
    ? "01—15"
    : selectedPeriod === "16-30"
      ? "16—30"
      : "Cały miesiąc";
  const periodLabel = `${_periodDisplay} ${MONTHS[selectedMonth - 1]} ${selectedYear}`;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Vertical Dark Sidebar */}
      <aside
        className={`${sidebarExpanded ? "w-56" : "w-14"} bg-[#0B1E3F] text-slate-300 flex flex-col py-3 gap-1 shrink-0 sticky top-0 h-screen z-30 overflow-y-auto transition-all duration-200`}
        data-testid="admin-sidebar"
      >
        <div className={`flex items-center ${sidebarExpanded ? "px-3" : "justify-center"} mb-3 shrink-0`}>
          <div className="w-8 h-8 border border-white/30 flex items-center justify-center rounded-md">
            <span className="font-heading font-bold text-sm text-white">A</span>
          </div>
          {sidebarExpanded && (
            <span className="ml-3 font-heading font-bold text-white text-base tracking-tight">AODEX</span>
          )}
        </div>
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} className="w-full flex flex-col gap-1 mb-2">
            {group.label && sidebarExpanded && (
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                {group.label}
              </div>
            )}
            {group.label && !sidebarExpanded && gi > 0 && (
              <div className="mx-3 border-t border-white/10 my-1" />
            )}
            {group.items.map(({ key, label, Icon }) => (
              <a
                key={key}
                href={key === "home" ? "?" : `?tab=${key}`}
                onClick={(e) => {
                  // Left-click without modifiers → SPA nav; let ctrl/cmd/middle-click open new tab
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                  e.preventDefault();
                  setTab(key);
                }}
                title={label}
                aria-label={label}
                data-testid={`tab-${key}`}
                className={`${sidebarExpanded ? "mx-2 px-3 justify-start gap-3" : "mx-2 justify-center"} h-10 flex items-center shrink-0 rounded-md transition-colors relative no-underline ${
                  tab === key
                    ? "bg-[#2563EB] text-white shadow-md"
                    : "text-slate-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" weight={tab === key ? "fill" : "regular"} />
                {sidebarExpanded && <span className="text-sm font-medium truncate">{label}</span>}
                {key === "chat" && chatUnread > 0 && (
                  <span
                    className={`${sidebarExpanded ? "ml-auto" : "absolute -top-1 -right-1"} min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center`}
                    data-testid="chat-badge"
                  >
                    {chatUnread > 99 ? "99+" : chatUnread}
                  </span>
                )}
              </a>
            ))}
          </div>
        ))}
        <div className="flex-1 min-h-[8px]" />
        <button
          onClick={() => setShowChangePassword(true)}
          title="Zmień hasło"
          aria-label="Zmień hasło"
          data-testid="change-password-btn"
          className={`${sidebarExpanded ? "mx-2 px-3 justify-start gap-3" : "mx-2 justify-center"} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
        >
          <Key className="w-5 h-5 shrink-0" />
          {sidebarExpanded && <span className="text-sm font-medium">Zmień hasło</span>}
        </button>
        <button
          onClick={logout}
          title="Wyloguj"
          aria-label="Wyloguj"
          data-testid="logout-btn"
          className={`${sidebarExpanded ? "mx-2 px-3 justify-start gap-3" : "mx-2 justify-center"} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
        >
          <SignOut className="w-5 h-5 shrink-0" />
          {sidebarExpanded && <span className="text-sm font-medium">Wyloguj</span>}
        </button>
        <div className="border-t border-white/10 mx-2 my-2" />
        <button
          onClick={() => setSidebarExpanded((v) => !v)}
          title={sidebarExpanded ? "Zwiń menu" : "Rozwiń menu"}
          aria-label={sidebarExpanded ? "Zwiń menu" : "Rozwiń menu"}
          data-testid="sidebar-toggle"
          className={`${sidebarExpanded ? "mx-2 px-3 justify-start gap-3" : "mx-2 justify-center"} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
        >
          {sidebarExpanded ? (
            <>
              <CaretDoubleLeft className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Zwiń menu</span>
            </>
          ) : (
            <CaretDoubleRight className="w-5 h-5 shrink-0" />
          )}
        </button>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Sticky Header — mockup style: greeting + period picker · search + moon + avatar */}
        <header
          className="sticky top-0 bg-white border-b border-border px-4 md:px-6 py-3 flex items-center justify-between gap-3 z-20"
          data-testid="admin-header"
        >
          {/* Left: greeting + period picker */}
          <div className="min-w-0 flex-1">
            <p className="text-xs md:text-sm text-muted-foreground">
              Witaj, <span className="font-medium text-foreground">{user?.name || "Admin"}</span>! 👋
            </p>
            <div className="mt-0.5">
              <button
                onClick={() => setShowMonthPicker((v) => !v)}
                className="inline-flex items-center gap-1 text-lg md:text-2xl font-bold tracking-tight text-foreground hover:opacity-80"
                data-testid="header-period-label"
              >
                {MONTHS[selectedMonth - 1]} {selectedYear}
                <CaretDown className="w-4 h-4" />
              </button>
              {showMonthPicker && (
                <div className="absolute mt-1 bg-white border border-border rounded-md shadow-lg z-30 p-3 grid grid-cols-3 gap-1 w-72" data-testid="month-picker">
                  <div className="col-span-3 flex items-center justify-between mb-2">
                    <button onClick={() => setSelectedYear(selectedYear - 1)} className="p-1 hover:bg-muted rounded" data-testid="year-prev">‹</button>
                    <span className="text-sm font-bold">{selectedYear}</span>
                    <button onClick={() => setSelectedYear(selectedYear + 1)} className="p-1 hover:bg-muted rounded" data-testid="year-next">›</button>
                  </div>
                  {MONTHS.map((mn, i) => (
                    <button
                      key={i}
                      onClick={() => { setSelectedMonth(i + 1); setShowMonthPicker(false); }}
                      className={`px-2 py-1.5 text-xs rounded-md ${selectedMonth === i + 1 ? "bg-[#2563EB] text-white" : "hover:bg-muted"}`}
                      data-testid={`month-opt-${i + 1}`}
                    >
                      {mn.slice(0, 3)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Search */}
            <div className="relative hidden sm:block">
              <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Szukaj..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                className="pl-9 pr-3 py-2 text-sm bg-slate-50 border border-border rounded-md w-40 md:w-64 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="header-search"
              />
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute right-0 mt-1 bg-white border border-border rounded-md shadow-lg w-80 max-h-96 overflow-y-auto z-30" data-testid="search-results">
                  {searchResults.map((r, i) => (
                    <button
                      key={`${r.type}-${r.id || i}`}
                      onClick={() => { setTab(r.tab); setShowSearchResults(false); setSearchQuery(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-muted border-b border-border last:border-0"
                      data-testid={`search-hit-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground w-16">{r.type}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{highlightMatch(r.title, searchQuery)}</div>
                          <div className="text-xs text-muted-foreground truncate">{highlightMatch(r.subtitle, searchQuery)}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Manual refresh */}
            <RefreshButton />
            {/* Notifications */}
            <NotificationBell />
            {/* Push subscribe */}
            <PushBell />
            <ThemeToggle />
            {/* Avatar */}
            <div className="hidden md:flex items-center gap-2 pl-2 border-l border-border">
              <div className="w-9 h-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-xs font-bold shrink-0">
                {(user?.name || "AD").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-foreground truncate max-w-[140px]" data-testid="admin-title">
                  {user?.name || "Admin"}
                </div>
                <div className="text-xs text-muted-foreground">Administrator</div>
              </div>
            </div>
          </div>
        </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-4 sm:px-6 py-6 w-full">
        {tab === "employees" ? (
          <EmployeesTab
            employees={employees}
            onAddEmployee={() => {
              setEditingEmployee(null);
              setEmployeeForm({
                name: "", email: "", password: "", imie_nazwisko: "",
                pesel: "", numer_paszportu: "", numer_prawa_jazdy: "",
                numer_blankietu_prawa_jazdy: "", adres: "", numer_telefonu: "",
                numer_telefonu_zebra: "", data_urodzenia: "",
                salary_config: { mode: "percent", percent: 50, rate: 0, daily_rate: 0 },
              });
              setShowEmployeeModal(true);
            }}
            onEditEmployee={openEditEmployee}
            onResetPassword={handleResetPassword}
            onDeleteEmployee={handleDeleteEmployee}
            onRefresh={fetchEmployees}
          />
        ) : tab === "salary" ? (
          <SalaryTab
            records={records}
            statsRecords={statsRecords}
            employees={employees}
            loading={loading}
            uploadingPDF={uploadingPDF}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            selectedPeriod={selectedPeriod}
            selectedEmployee={selectedEmployee}
            onYearChange={setSelectedYear}
            onMonthChange={setSelectedMonth}
            onPeriodChange={setSelectedPeriod}
            onEmployeeChange={setSelectedEmployee}
            onPDFUpload={handlePDFUpload}
            onExportPDF={handleExportPDF}
            onAddSalary={openAddSalary}
            onEditSalary={openEditSalary}
            onDeleteSalary={handleDeleteSalary}
          />
        ) : null}

        {tab === "obligations" && (
          <div className="space-y-4 px-3 sm:px-0" data-testid="obligations-tab">
            {/* Sub-tabs strip */}
            <div className="flex items-center gap-2 border-b border-border overflow-x-auto">
              <button
                data-testid="obligations-subtab-private"
                onClick={() => setObligationsSubTab("private-credits")}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  obligationsSubTab === "private-credits"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Kredyty prywatne
              </button>
              <button
                data-testid="obligations-subtab-corporate"
                onClick={() => setObligationsSubTab("corporate-credits")}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  obligationsSubTab === "corporate-credits"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Kredyty firmowe
              </button>
              <button
                data-testid="obligations-subtab-leasings"
                onClick={() => setObligationsSubTab("leasings")}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  obligationsSubTab === "leasings"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Leasingi
              </button>
              <button
                data-testid="obligations-subtab-uslugi"
                onClick={() => setObligationsSubTab("uslugi")}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  obligationsSubTab === "uslugi"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Usługi sieciowe
              </button>
            </div>

            {obligationsSubTab === "private-credits" && (
              <PrivateCreditsTab
                privateCredits={privateCredits}
                loading={loading}
                onAddCredit={openAddCredit}
                onEditCredit={openEditCredit}
                onDeleteCredit={handleDeleteCredit}
                onRefresh={fetchPrivateCredits}
              />
            )}
            {obligationsSubTab === "corporate-credits" && (
              <CorporateCreditsTab
                corporateCredits={corporateCredits}
                loading={loading}
                onAddCredit={openAddCredit}
                onEditCredit={openEditCredit}
                onDeleteCredit={handleDeleteCredit}
                onRefresh={fetchCorporateCredits}
              />
            )}
            {obligationsSubTab === "leasings" && (
              <LeasingsTab
                leasings={leasings}
                loading={loading}
                onAddLeasing={openAddCredit}
                onEditLeasing={openEditCredit}
                onDeleteLeasing={handleDeleteCredit}
                onRefresh={fetchLeasings}
              />
            )}
            {obligationsSubTab === "uslugi" && <UslugiSiecioweTab />}
          </div>
        )}

        {tab === "documents" && (
          <DocumentsTab
            documents={documents}
            loading={loading}
            onViewDocument={handleViewDocument}
          />
        )}

        {tab === "uro" && <UroTab />}
        
        {tab === "pojazdy" && <PojazdyTab />}
        
        {tab === "urlopy" && <UrlowyTab />}
        {tab === "dochod" && <DochodFirmyTab />}
        {tab === "ksef" && <KsefTab />}
        {tab === "notifications" && <NotificationsTab />}
        {tab === "audit" && <AuditLogTab />}
        {tab === "pozyczki" && <PozyczkiPracownikowTab />}
        {tab === "work-hours" && <WorkHoursTab />}
        {tab === "urzadzenia" && <UrzadzeniaTab />}
        {tab === "kontakty" && <KontaktyTab />}
        {tab === "rejony" && <RejonyTab />}
        {tab === "przypomnienia" && <PrzypomnieniaTab />}
        {tab === "dyzury" && <DyzuryTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "home" && <DashboardHome onNavigate={(t) => setTab(t)} selectedYear={selectedYear} selectedMonth={selectedMonth} />}
        {tab === "chat" && <ChatTab user={user} />}
        {tab === "poczta" && <PocztaTab />}
        {tab === "dniowki" && <DniowkiTab />}
        {tab === "backup" && <BackupTab />}
      </main>
      </div>{/* /main column */}

      {/* Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-sm w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
            data-testid="employee-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">
                {editingEmployee ? "Edytuj" : "Nowy pracownik"}
              </h3>
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="p-2 hover:bg-muted rounded-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="field-label block mb-2">Skrót</label>
                <input
                  value={employeeForm.name}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="input-field"
                  data-testid="employee-name-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Email</label>
                <input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="input-field"
                  data-testid="employee-email-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Imię i nazwisko (do raportów)</label>
                <input
                  type="text"
                  value={employeeForm.imie_nazwisko}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      imie_nazwisko: e.target.value,
                    }))
                  }
                  placeholder="np. Jan Kowalski"
                  className="input-field"
                  data-testid="employee-imie-nazwisko-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Używane w eksportach (Ewidencja godzin XLSX/PDF). Jeśli puste — używany jest skrót.
                </p>
              </div>

              {/* Personal data section */}
              <div className="pt-4 mt-2 border-t border-slate-200">
                <div className="text-sm font-semibold text-slate-700 mb-3">Dane osobowe</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label block mb-2">PESEL</label>
                    <input
                      type="text"
                      value={employeeForm.pesel}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, pesel: e.target.value }))}
                      placeholder="np. 85010112345"
                      className="input-field"
                      data-testid="employee-pesel-input"
                    />
                  </div>
                  <div>
                    <label className="field-label block mb-2">Data urodzenia</label>
                    <input
                      type="date"
                      value={employeeForm.data_urodzenia}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, data_urodzenia: e.target.value }))}
                      className="input-field"
                      data-testid="employee-data-urodzenia-input"
                    />
                  </div>
                  <div>
                    <label className="field-label block mb-2">Numer telefonu (komórka)</label>
                    <input
                      type="text"
                      value={employeeForm.numer_telefonu}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, numer_telefonu: e.target.value }))}
                      placeholder="np. +48 555 123 456"
                      className="input-field"
                      data-testid="employee-phone-input"
                    />
                  </div>
                  <div>
                    <label className="field-label block mb-2">Numer telefonu (Zebra)</label>
                    <input
                      type="text"
                      value={employeeForm.numer_telefonu_zebra}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, numer_telefonu_zebra: e.target.value }))}
                      placeholder="np. +48 555 987 654"
                      className="input-field"
                      data-testid="employee-phone-zebra-input"
                    />
                  </div>
                  <div>
                    <label className="field-label block mb-2">Numer paszportu</label>
                    <input
                      type="text"
                      value={employeeForm.numer_paszportu}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, numer_paszportu: e.target.value }))}
                      className="input-field"
                      data-testid="employee-passport-input"
                    />
                  </div>
                  <div>
                    <label className="field-label block mb-2">Numer prawa jazdy</label>
                    <input
                      type="text"
                      value={employeeForm.numer_prawa_jazdy}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, numer_prawa_jazdy: e.target.value }))}
                      className="input-field"
                      data-testid="employee-driving-license-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label block mb-2">Numer blankietu prawa jazdy</label>
                    <input
                      type="text"
                      value={employeeForm.numer_blankietu_prawa_jazdy}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, numer_blankietu_prawa_jazdy: e.target.value }))}
                      className="input-field"
                      data-testid="employee-driving-blanket-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label block mb-2">Adres</label>
                    <input
                      type="text"
                      value={employeeForm.adres}
                      onChange={(e) => setEmployeeForm((prev) => ({ ...prev, adres: e.target.value }))}
                      placeholder="ul. Przykładowa 1, 00-000 Warszawa"
                      className="input-field"
                      data-testid="employee-address-input"
                    />
                  </div>
                </div>
              </div>
              {!editingEmployee && (
                <div>
                  <label className="field-label block mb-2">Hasło</label>
                  <input
                    type="password"
                    value={employeeForm.password}
                    onChange={(e) =>
                      setEmployeeForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="input-field"
                    data-testid="employee-password-input"
                  />
                </div>
              )}

              {/* System wynagrodzenia */}
              <div className="pt-4 mt-2 border-t border-slate-200">
                <div className="text-sm font-semibold text-slate-700 mb-3">System wynagrodzenia</div>
                <SalaryConfigSection
                  value={employeeForm.salary_config}
                  onChange={(cfg) => setEmployeeForm((prev) => ({ ...prev, salary_config: cfg }))}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="btn-secondary flex-1"
              >
                Anuluj
              </button>
              <button
                onClick={handleSaveEmployee}
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-employee-btn"
              >
                {saving ? (
                  <CircleNotch className="w-4 h-4 animate-spin" />
                ) : (
                  <FloppyDisk className="w-4 h-4" />
                )}
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Salary Modal */}
      {showSalaryModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-sm w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
            data-testid="salary-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">
                {editingSalary ? "Edytuj wpis" : "Nowy wpis"}
              </h3>
              <button
                onClick={() => setShowSalaryModal(false)}
                className="p-2 hover:bg-muted rounded-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {!editingSalary && (
                <>
                  <div className="col-span-2">
                    <label className="field-label block mb-2">Pracownik</label>
                    <select
                      value={salaryForm.employee_id}
                      onChange={(e) =>
                        setSalaryForm((prev) => ({
                          ...prev,
                          employee_id: e.target.value,
                        }))
                      }
                      className="input-field"
                      data-testid="salary-employee-select"
                    >
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label block mb-2">Okres</label>
                    <select
                      value={`${salaryForm.period_start}-${salaryForm.period_end}`}
                      onChange={(e) => {
                        const [start, end] = e.target.value.split("-");
                        setSalaryForm((prev) => ({
                          ...prev,
                          period_start: start,
                          period_end: end,
                        }));
                      }}
                      className="input-field"
                      data-testid="salary-period-select"
                    >
                      <option value="01-15">01-15</option>
                      <option value="16-30">16-30</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label block mb-2">Miesiąc</label>
                    <select
                      value={salaryForm.month}
                      onChange={(e) =>
                        setSalaryForm((prev) => ({
                          ...prev,
                          month: Number(e.target.value),
                        }))
                      }
                      className="input-field"
                      data-testid="salary-month-select"
                    >
                      {MONTHS.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label block mb-2">Rok</label>
                    <select
                      value={salaryForm.year}
                      onChange={(e) =>
                        setSalaryForm((prev) => ({
                          ...prev,
                          year: Number(e.target.value),
                        }))
                      }
                      className="input-field"
                      data-testid="salary-year-select"
                    >
                      {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {isFixedRateMode && (
                <div className="col-span-2">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-100 text-purple-800" data-testid="salary-form-mode-badge">
                      Stała stawka
                    </span>
                    <span className="text-xs text-slate-500">
                      Uproszczony formularz — bez pól VAT / dopłaty
                    </span>
                  </div>
                  <label className="field-label block mb-2">
                    Wynagrodzenie stałe (zł)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={salaryForm.wynagrodzenie_stale === "" ? "" : salaryForm.wynagrodzenie_stale}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSalaryForm((prev) => {
                        const numValue = val === "" ? "" : parseFloat(val) || 0;
                        const updated = { ...prev, wynagrodzenie_stale: numValue };
                        return { ...updated, ...calculateFormulas(updated, "rate") };
                      });
                    }}
                    className="input-field"
                    data-testid="salary-wynagrodzenie_stale-input"
                  />
                </div>
              )}
              {ALL_FIELDS
                .filter((f) => {
                  // Hide paliwo/rata/paliwo_total — they are now line items inside Potrącenia
                  if (["paliwo", "rata", "paliwo_total"].includes(f.key)) return false;
                  // In "rate" mode: also hide VAT/dopłata/przychód
                  if (isFixedRateMode && ["przychod_vat_brutto", "doplata_paliwowa", "przychod"].includes(f.key)) return false;
                  return true;
                })
                .map((f) => (
                <div key={f.key}>
                  <label className="field-label block mb-2">
                    {f.label}
                    {f.hasNote && (
                      <input
                        type="text"
                        value={salaryForm.rata_info || ""}
                        onChange={(e) =>
                          setSalaryForm((prev) => ({
                            ...prev,
                            rata_info: e.target.value,
                          }))
                        }
                        placeholder="5/12"
                        className="ml-2 w-20 px-2 py-0.5 text-xs border border-border rounded-sm inline-block"
                        data-testid="salary-rata_info-input"
                      />
                    )}
                    {f.formula && (
                      <span className="text-blue-500 ml-1">(авто)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={salaryForm[f.key] === "" ? "" : salaryForm[f.key]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSalaryForm((prev) => {
                        // Allow empty string for clearing, convert to number otherwise
                        const numValue = val === "" ? "" : parseFloat(val) || 0;
                        const updated = { ...prev, [f.key]: numValue };
                        if (f.editable) {
                          const calculated = calculateFormulas(updated, effectiveSalaryMode);
                          return { ...updated, ...calculated };
                        }
                        return updated;
                      });
                    }}
                    className="input-field"
                    disabled={!f.editable}
                    data-testid={`salary-${f.key}-input`}
                  />
                </div>
              ))}
            </div>

            {/* Paliwo pozycje merged into unified Potrącenia list below */}

            {/* Auto-uzupełnij section was removed — items are now applied
                automatically the moment the modal opens (see effect above). */}

            {/* Potrącenia Section — unified list: paliwo + raty + pożyczki + usługi */}
            <div className="mt-6 pt-6 border-t border-border" data-testid="potracenia-section">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-sm">Potrącenia</h4>
                {previewLoading && (
                  <span className="text-[10px] text-slate-500 italic">Ładuję z bazy…</span>
                )}
              </div>
              
              {salaryForm.custom_deductions && salaryForm.custom_deductions.length > 0 && (
                <div className="space-y-2">
                  {salaryForm.custom_deductions.map((deduction, index) => (
                    deduction && deduction.locked ? (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200"
                        data-testid={`deduction-locked-${index}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span
                            className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wide shrink-0"
                            title="Dodane automatycznie przez system"
                          >
                            {deduction.source === "paliwo" ? "Paliwo" :
                             deduction.source === "rata_kredyt" ? "Rata" :
                             deduction.source === "pozyczka" ? "Pożyczka" :
                             deduction.source === "usluga" ? "Usługa" :
                             "Auto"}
                          </span>
                          <span className="text-sm text-slate-700 truncate" title={deduction.name}>
                            {deduction.name || "—"}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-red-600 tabular-nums whitespace-nowrap">
                          − {(parseFloat(deduction.amount) || 0).toFixed(2)} zł
                        </span>
                      </div>
                    ) : (
                      <div key={index} className="flex gap-2 items-start" data-testid={`deduction-manual-${index}`}>
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Nazwa (np. pożyczka)"
                            value={deduction.name}
                            onChange={(e) => {
                              const updated = [...salaryForm.custom_deductions];
                              updated[index].name = e.target.value;
                              setSalaryForm(prev => {
                                const newForm = { ...prev, custom_deductions: updated };
                                const calculated = calculateFormulas(newForm, effectiveSalaryMode);
                                return { ...newForm, ...calculated };
                              });
                            }}
                            className="input-field text-sm"
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Kwota"
                            value={deduction.amount === 0 ? '' : deduction.amount}
                            onChange={(e) => {
                              const updated = [...salaryForm.custom_deductions];
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                              updated[index].amount = value;
                              setSalaryForm(prev => {
                                const newForm = { ...prev, custom_deductions: updated };
                                const calculated = calculateFormulas(newForm, effectiveSalaryMode);
                                return { ...newForm, ...calculated };
                              });
                            }}
                            className="input-field text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = salaryForm.custom_deductions.filter((_, i) => i !== index);
                            setSalaryForm(prev => {
                              const newForm = { ...prev, custom_deductions: updated };
                              const calculated = calculateFormulas(newForm, effectiveSalaryMode);
                              return { ...newForm, ...calculated };
                            });
                          }}
                          className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                          title="Usuń"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    )
                  ))}
                </div>
              )}
              
              {salaryForm.custom_deductions && salaryForm.custom_deductions.length > 0 && (
                <div className="mt-3 text-sm text-gray-600">
                  <span className="font-medium">Suma potrąceń:</span>{" "}
                  <span className="font-semibold text-red-600">
                    {salaryForm.custom_deductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0).toFixed(2)} zł
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSalaryModal(false)}
                className="btn-secondary flex-1"
              >
                Anuluj
              </button>
              <button
                onClick={handleSaveSalary}
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-salary-btn"
              >
                {saving ? (
                  <CircleNotch className="w-4 h-4 animate-spin" />
                ) : (
                  <FloppyDisk className="w-4 h-4" />
                )}
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal (Large modal with auto-calculator) */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] flex flex-col"
            data-testid="credit-modal"
          >
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <h3 className="font-heading text-xl font-bold">
                {editingCredit ? "Edytuj" : "Nowy"}{" "}
                {creditType === "private"
                  ? "kredyt prywatny"
                  : creditType === "corporate"
                    ? "kredyt firmowy"
                    : "leasing"}
              </h3>
              <button
                onClick={() => setShowCreditModal(false)}
                className="p-2 hover:bg-muted rounded-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {creditType === "leasing" && (
                  <div className="border border-border bg-muted rounded-sm p-4">
                    <label
                      htmlFor="leasing-pojazd-select"
                      className="field-label block mb-2 text-blue-900"
                    >
                      Wybierz pojazd z listy „Pojazdy"
                    </label>
                    <select
                      id="leasing-pojazd-select"
                      value={creditForm.pojazd_id || ""}
                      onChange={(e) => {
                        const id = e.target.value;
                        const p = pojazdy.find((x) => x.id === id);
                        setCreditForm((prev) => ({
                          ...prev,
                          pojazd_id: id,
                          name: p
                            ? [
                                p.nazwa,
                                p.marka && p.model
                                  ? `${p.marka} ${p.model}`
                                  : p.marka || p.model,
                                p.nr_rejestracyjny,
                              ]
                                .filter(Boolean)
                                .join(" • ")
                            : prev.name,
                        }));
                      }}
                      className="input-field"
                      data-testid="leasing-pojazd-select"
                    >
                      <option value="">— Wpisz ręcznie poniżej —</option>
                      {pojazdy.map((p) => (
                        <option key={p.id} value={p.id}>
                          {[
                            p.nazwa,
                            p.marka && p.model
                              ? `${p.marka} ${p.model}`
                              : p.marka || p.model,
                            p.nr_rejestracyjny,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-foreground mt-2">
                      Po wyborze pojazdu pole „Pojazd" zostanie wypełnione
                      automatycznie. Możesz nadal edytować nazwę ręcznie.
                    </div>
                  </div>
                )}

                <div>
                  <label className="field-label block mb-2">
                    {creditType === "leasing" ? "Pojazd" : "Nazwa"}
                  </label>
                  <input
                    type="text"
                    value={creditForm.name}
                    onChange={(e) =>
                      setCreditForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="input-field"
                    placeholder={
                      creditType === "leasing" ? "np. BMW X5" : "np. Passat"
                    }
                    data-testid="credit-name-input"
                  />
                </div>

                {(creditType === "corporate" || creditType === "leasing") && (
                  <div className="border border-amber-200 bg-amber-50 rounded-sm p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={creditForm.auto_sync_koszty}
                        onChange={(e) =>
                          setCreditForm((prev) => ({
                            ...prev,
                            auto_sync_koszty: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 mt-0.5 rounded border-gray-300"
                        data-testid="credit-auto-sync-koszty"
                      />
                      <div>
                        <div className="font-medium text-sm text-amber-900">
                          Automatycznie rozlicz raty w Kosztach (Dochód firmy)
                        </div>
                        <div className="text-xs text-amber-700 mt-1">
                          Wszystkie raty (opłacone i nieopłacone) zostaną dodane
                          do zakładki „Dochód firmy → Koszty" w odpowiednim
                          miesiącu. Opłacone raty pozostają jako historyczny
                          koszt (wyróżnione zielonym znacznikiem).
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                {creditType === "private" && (
                  <div>
                    <label className="field-label block mb-2">Pracownik</label>
                    <select
                      value={creditForm.employee_id}
                      onChange={(e) =>
                        setCreditForm((prev) => ({
                          ...prev,
                          employee_id: e.target.value,
                        }))
                      }
                      className="input-field"
                      data-testid="credit-employee-select"
                    >
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {creditType === "private" && creditForm.employee_id && (
                  <div className="border border-border bg-muted rounded-sm p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="add_to_salary"
                        checked={creditForm.add_to_salary}
                        onChange={(e) =>
                          setCreditForm((prev) => ({
                            ...prev,
                            add_to_salary: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <label
                        htmlFor="add_to_salary"
                        className="font-medium text-sm cursor-pointer"
                      >
                        Automatycznie dodawać ratę do wypłat?
                      </label>
                    </div>

                    {creditForm.add_to_salary && (
                      <div>
                        <label className="field-label block mb-2 text-sm">
                          W jakim okresie miesiąca?
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="salary_period"
                              value="01-15"
                              checked={creditForm.salary_period === "01-15"}
                              onChange={(e) =>
                                setCreditForm((prev) => ({
                                  ...prev,
                                  salary_period: e.target.value,
                                }))
                              }
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">01-15</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="salary_period"
                              value="16-30"
                              checked={creditForm.salary_period === "16-30"}
                              onChange={(e) =>
                                setCreditForm((prev) => ({
                                  ...prev,
                                  salary_period: e.target.value,
                                }))
                              }
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">16-30</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Raty</h4>
                    <div className="flex gap-2 flex-wrap">
                      <input
                        ref={harmonogramInputRef}
                        type="file"
                        accept=".pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        onChange={(e) =>
                          handleUploadHarmonogram(e.target.files?.[0])
                        }
                        data-testid="harmonogram-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => harmonogramInputRef.current?.click()}
                        disabled={uploadingHarmonogram}
                        className="px-3 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors text-sm flex items-center gap-1 disabled:opacity-50"
                        data-testid="upload-harmonogram-btn"
                        title="Wgraj harmonogram spłat (PDF / CSV / XLSX)"
                      >
                        {uploadingHarmonogram ? (
                          <CircleNotch className="w-4 h-4 animate-spin" />
                        ) : (
                          <FilePdf className="w-4 h-4" />
                        )}
                        {uploadingHarmonogram
                          ? "Wgrywam..."
                          : "Wgraj harmonogram"}
                      </button>
                      {creditType === "leasing" && (
                        <>
                          <button
                            type="button"
                            onClick={applyVATToPayments}
                            className="px-3 py-2 bg-violet-600 text-white rounded-sm hover:bg-violet-700 transition-colors text-sm flex items-center gap-1"
                            data-testid="apply-vat-btn"
                            title="Zastosuj +23% VAT do wszystkich rat"
                          >
                            +23% VAT
                          </button>
                          <button
                            type="button"
                            onClick={togglePolisaSection}
                            className={`px-3 py-2 rounded-sm text-sm flex items-center gap-1 transition-colors ${
                              creditForm.show_polisa
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"
                                : "bg-emerald-600 text-white hover:bg-emerald-700"
                            }`}
                            data-testid="toggle-polisa-btn"
                            title="Dodaj raty za polisę — sumowane z ratą leasingu w Dochód firmy"
                          >
                            {creditForm.show_polisa
                              ? "Ukryj polisę"
                              : "Dodaj ratę za polisę"}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowAutoCalc(!showAutoCalc)}
                        className="px-3 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <CircleNotch className="w-4 h-4" />
                        {showAutoCalc ? "Ukryj" : "Auto"}
                      </button>
                      <button
                        type="button"
                        onClick={addPayment}
                        className="btn-secondary flex items-center gap-2 text-sm"
                        data-testid="add-payment-btn"
                      >
                        <Plus className="w-4 h-4" /> Dodaj ratę
                      </button>
                    </div>
                  </div>

                  {showAutoCalc && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-sm">
                      <h4 className="font-semibold mb-3 text-sm">
                        Automatyczne generowanie rat
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Suma kredytu
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={autoCalcForm.total_amount}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                total_amount: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                            placeholder="54000"
                          />
                        </div>
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Liczba rat
                          </label>
                          <input
                            type="number"
                            value={autoCalcForm.number_of_payments}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                number_of_payments: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                            placeholder="36"
                          />
                        </div>
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Rata miesięczna
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={autoCalcForm.monthly_payment}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                monthly_payment: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                            placeholder="1500"
                          />
                        </div>
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Dzień płatności
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            value={autoCalcForm.payment_day}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                payment_day: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                          />
                        </div>
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Start miesiąc
                          </label>
                          <select
                            value={autoCalcForm.start_month}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                start_month: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                          >
                            {MONTHS.map((m, i) => (
                              <option key={i} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="field-label block mb-1 text-xs">
                            Start rok
                          </label>
                          <select
                            value={autoCalcForm.start_year}
                            onChange={(e) =>
                              setAutoCalcForm((prev) => ({
                                ...prev,
                                start_year: e.target.value,
                              }))
                            }
                            className="input-field text-sm"
                          >
                            {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={generatePayments}
                        className="mt-3 w-full bg-green-600 text-white py-2 rounded-sm hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        Generuj raty
                      </button>
                    </div>
                  )}

                  {creditForm.payments.length > 0 && (
                    <>
                      <div className="space-y-3 max-h-[300px] overflow-y-auto border rounded-sm p-3">
                        {creditForm.payments.map((payment, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-12 gap-2 items-center bg-muted p-3 rounded-sm"
                          >
                            <div className="col-span-1 font-bold text-sm">
                              #{payment.payment_number}
                            </div>
                            <div className="col-span-11 grid grid-cols-1 md:grid-cols-5 gap-2">
                              <div>
                                <input
                                  type="text"
                                  value={payment.date}
                                  onChange={(e) =>
                                    updatePayment(index, "date", e.target.value)
                                  }
                                  className="input-field text-sm"
                                  placeholder="20.01.2026"
                                  data-testid={`payment-${index}-date`}
                                />
                              </div>
                              <div>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={payment.amount}
                                  onChange={(e) =>
                                    updatePayment(
                                      index,
                                      "amount",
                                      e.target.value === ""
                                        ? ""
                                        : parseFloat(e.target.value),
                                    )
                                  }
                                  className="input-field text-sm"
                                  placeholder="Rata"
                                  data-testid={`payment-${index}-amount`}
                                />
                              </div>
                              <div>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={payment.odsetki ?? ""}
                                  onChange={(e) =>
                                    updatePayment(
                                      index,
                                      "odsetki",
                                      e.target.value === ""
                                        ? ""
                                        : parseFloat(e.target.value),
                                    )
                                  }
                                  className="input-field text-sm"
                                  placeholder="Odsetki"
                                  data-testid={`payment-${index}-odsetki`}
                                />
                              </div>
                              <div>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={payment.remaining}
                                  onChange={(e) =>
                                    updatePayment(
                                      index,
                                      "remaining",
                                      e.target.value === ""
                                        ? ""
                                        : parseFloat(e.target.value),
                                    )
                                  }
                                  className="input-field text-sm"
                                  placeholder="Pozostało"
                                  data-testid={`payment-${index}-remaining`}
                                />
                              </div>
                              <div className="flex items-end gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={payment.paid}
                                    onChange={(e) =>
                                      updatePayment(
                                        index,
                                        "paid",
                                        e.target.checked,
                                      )
                                    }
                                    className="w-4 h-4"
                                    data-testid={`payment-${index}-paid`}
                                  />
                                  <span className="text-xs field-label">
                                    Opłacono
                                  </span>
                                </label>
                                {payment.nadplata && (
                                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid={`payment-${index}-nadplata`}>
                                    Nadpłata
                                  </span>
                                )}
                                <button
                                  onClick={() => removePayment(index)}
                                  className="p-2 hover:bg-red-50 text-red-600 rounded-sm ml-auto"
                                  data-testid={`payment-${index}-remove`}
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalOdsetki = creditForm.payments.reduce(
                          (s, p) => s + (Number(p.odsetki) || 0),
                          0,
                        );
                        const pozostaloDoSplaty = creditForm.payments
                          .filter((p) => !p.paid)
                          .reduce((s, p) => s + (Number(p.amount) || 0), 0);
                        const totalAmount = creditForm.payments.reduce(
                          (s, p) => s + (Number(p.amount) || 0),
                          0,
                        );
                        const fmt = (v) =>
                          v.toLocaleString("pl-PL", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });
                        return (
                          <div
                            className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3"
                            data-testid="payments-totals"
                          >
                            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                                Suma rat (całkowita)
                              </div>
                              <div
                                className="text-lg font-bold text-slate-800 tabular-nums mt-1"
                                data-testid="total-amount"
                              >
                                {fmt(totalAmount)} zł
                              </div>
                            </div>
                            <div className="bg-muted border border-border rounded-sm p-3">
                              <div className="text-xs text-foreground uppercase tracking-wider font-semibold">
                                Suma odsetek
                              </div>
                              <div
                                className="text-lg font-bold text-blue-800 tabular-nums mt-1"
                                data-testid="total-odsetki"
                              >
                                {fmt(totalOdsetki)} zł
                              </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                              <div className="text-xs text-amber-700 uppercase tracking-wider font-semibold">
                                Pozostało do spłaty
                              </div>
                              <div
                                className="text-lg font-bold text-amber-800 tabular-nums mt-1"
                                data-testid="total-remaining-to-pay"
                              >
                                {fmt(pozostaloDoSplaty)} zł
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {/* ====================== POLISA SECTION ====================== */}
                  {creditType === "leasing" && creditForm.show_polisa && (
                    <div
                      className="mt-6 border border-emerald-200 bg-emerald-50/40 rounded-sm p-4"
                      data-testid="polisa-section"
                    >
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h4 className="font-semibold text-emerald-900">
                          Raty za polisę (ubezpieczenie)
                        </h4>
                        <div className="flex gap-2 flex-wrap">
                          <input
                            ref={polisaHarmonogramInputRef}
                            type="file"
                            accept=".pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            onChange={(e) =>
                              handleUploadHarmonogram(
                                e.target.files?.[0],
                                "polisa_payments",
                              )
                            }
                            data-testid="polisa-harmonogram-input"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              polisaHarmonogramInputRef.current?.click()
                            }
                            disabled={uploadingHarmonogram}
                            className="px-3 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors text-sm flex items-center gap-1 disabled:opacity-50"
                            data-testid="upload-polisa-harmonogram-btn"
                            title="Wgraj harmonogram polisy (PDF / CSV / XLSX)"
                          >
                            <FilePdf className="w-4 h-4" /> Wgraj polisę
                          </button>
                          <button
                            type="button"
                            onClick={addPolisaPayment}
                            className="btn-secondary text-sm flex items-center gap-2"
                            data-testid="add-polisa-payment-btn"
                          >
                            <Plus className="w-4 h-4" /> Dodaj ratę polisy
                          </button>
                        </div>
                      </div>

                      {creditForm.polisa_payments.length === 0 ? (
                        <div className="text-sm text-emerald-700">
                          Brak rat polisy. Dodaj ratę ręcznie lub wgraj
                          harmonogram polisy.
                        </div>
                      ) : (
                        <>
                          <div className="space-y-3 max-h-[260px] overflow-y-auto border bg-white rounded-sm p-3">
                            {creditForm.polisa_payments.map((payment, index) => (
                              <div
                                key={index}
                                className="grid grid-cols-12 gap-2 items-center bg-emerald-50/60 p-3 rounded-sm"
                              >
                                <div className="col-span-1 font-bold text-sm text-emerald-900">
                                  P#{index + 1}
                                </div>
                                <div className="col-span-11 grid grid-cols-1 md:grid-cols-4 gap-2">
                                  <input
                                    type="text"
                                    value={payment.date}
                                    onChange={(e) =>
                                      updatePolisaPayment(
                                        index,
                                        "date",
                                        e.target.value,
                                      )
                                    }
                                    className="input-field text-sm"
                                    placeholder="20.01.2026"
                                    data-testid={`polisa-payment-${index}-date`}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={payment.amount}
                                    onChange={(e) =>
                                      updatePolisaPayment(
                                        index,
                                        "amount",
                                        e.target.value === ""
                                          ? ""
                                          : parseFloat(e.target.value),
                                      )
                                    }
                                    className="input-field text-sm"
                                    placeholder="Kwota polisy"
                                    data-testid={`polisa-payment-${index}-amount`}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={payment.remaining}
                                    onChange={(e) =>
                                      updatePolisaPayment(
                                        index,
                                        "remaining",
                                        e.target.value === ""
                                          ? ""
                                          : parseFloat(e.target.value),
                                      )
                                    }
                                    className="input-field text-sm"
                                    placeholder="Pozostało"
                                    data-testid={`polisa-payment-${index}-remaining`}
                                  />
                                  <div className="flex items-end gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={payment.paid}
                                        onChange={(e) =>
                                          updatePolisaPayment(
                                            index,
                                            "paid",
                                            e.target.checked,
                                          )
                                        }
                                        className="w-4 h-4"
                                        data-testid={`polisa-payment-${index}-paid`}
                                      />
                                      <span className="text-xs field-label">
                                        Opłacono
                                      </span>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removePolisaPayment(index)
                                      }
                                      className="p-2 hover:bg-red-50 text-red-600 rounded-sm ml-auto"
                                      data-testid={`polisa-payment-${index}-remove`}
                                    >
                                      <Trash className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {(() => {
                            const sumPolisa =
                              creditForm.polisa_payments.reduce(
                                (s, p) => s + (Number(p.amount) || 0),
                                0,
                              );
                            const pozostaloPolisa = creditForm.polisa_payments
                              .filter((p) => !p.paid)
                              .reduce(
                                (s, p) => s + (Number(p.amount) || 0),
                                0,
                              );
                            const fmt = (v) =>
                              v.toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              });
                            return (
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-white border border-emerald-200 rounded-sm p-3">
                                  <div className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">
                                    Suma rat polisy
                                  </div>
                                  <div
                                    className="text-lg font-bold text-emerald-800 tabular-nums mt-1"
                                    data-testid="polisa-total-amount"
                                  >
                                    {fmt(sumPolisa)} zł
                                  </div>
                                </div>
                                <div className="bg-white border border-amber-200 rounded-sm p-3">
                                  <div className="text-xs text-amber-700 uppercase tracking-wider font-semibold">
                                    Pozostało polisy
                                  </div>
                                  <div
                                    className="text-lg font-bold text-amber-800 tabular-nums mt-1"
                                    data-testid="polisa-total-remaining"
                                  >
                                    {fmt(pozostaloPolisa)} zł
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="border-t border-border p-6 flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreditModal(false)}
                  className="btn-secondary flex-1"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSaveCredit}
                  disabled={
                    saving ||
                    !creditForm.name ||
                    creditForm.payments.length === 0
                  }
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="save-credit-btn"
                >
                  {saving ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                  ) : (
                    <FloppyDisk className="w-4 h-4" />
                  )}
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {newPassword && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md p-6">
            <h3 className="font-heading text-xl font-bold mb-4">
              Nowe hasło wygenerowane
            </h3>
            <p className="mb-4">Nowe hasło dla pracownika:</p>
            <div className="bg-muted p-4 rounded-sm font-mono text-lg font-bold text-center mb-6">
              {newPassword}
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Zapisz to hasło. Nie będzie ponownie wyświetlone.
            </p>
            <button
              onClick={() => setNewPassword(null)}
              className="btn-primary w-full"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          isOpen={showChangePassword}
          onClose={() => setShowChangePassword(false)}
          isRequired={user?.password_change_required}
        />
      )}
    </div>
  );
}
